import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Calendar } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useGetData } from "@/hooks/services/use-query-hooks";
import { TechnicianGroupedByShop } from "@/hooks/services/pps/handlers/technicians-api";
import { GroupedTechnician } from "@/hooks/services/pps/types/technicians.type";

import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Filter } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useWorkSpaceStore } from "@/stores/store";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { SearchFilterBar } from "@/components/search-filter-bar";

export const Route = createFileRoute(
  "/plant-3/(technician-screens)/technicians/grouped",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const workspaceId = useWorkSpaceStore((state) => state.workspace.id);
  const [searchQuery, setSearchQuery] = useState("");
  const [shopFilter, setShopFilter] = useState("");

  const { data, isLoading, isRefetching } = useGetData(
    TechnicianGroupedByShop({ workspaceId }),
  );

  const filteredData = (data?.data as GroupedTechnician[])?.filter((shop) => {
    if (shopFilter && shopFilter !== "all" && shop.shopName !== shopFilter)
      return false;
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      return (
        shop.shopName.toLowerCase().includes(searchLower) ||
        shop.technicians.some((tech) =>
          tech.name.toLowerCase().includes(searchLower),
        )
      );
    }
    return true;
  });

  if (isLoading || isRefetching) {
    return (
      <div className="aero:flex aero:items-center aero:justify-center aero:h-64">
        <div className="aero:text-lg">Loading technicians...</div>
      </div>
    );
  }

  return (
    <>
      <SearchFilterBar
        placeholder="Search technicians by name..."
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        addOnFilter={[
          <Select
            value={shopFilter || undefined}
            onValueChange={(value) => setShopFilter(value || "")}
          >
            <SelectTrigger className="aero:w-[200px]">
              <Filter className="aero:mr-2 aero:h-4 aero:w-4" />
              <SelectValue placeholder="Filter by Shop" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Shops</SelectItem>
              {(data?.data as GroupedTechnician[])?.map((shop) => (
                <SelectItem key={shop.id} value={shop.shopName}>
                  {shop.shopName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>,
        ]}
      />
      <Card className="aero:!rounded-lg aero:!border-neutral-200 aero:!p-0 aero:!gap-0 aero:!shadow-none aero:w-full aero:min-w-0 aero:!overflow-hidden">
        <CardHeader className="aero:!py-2 aero:!px-4">
          <div className="aero:flex aero:flex-col aero:lg:flex-row aero:items-start aero:justify-between aero:gap-4">
            <div className="aero:flex aero:items-center aero:gap-2">
              <span className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
              <h2 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
                Technician Grouped by Shop
              </h2>
            </div>
          </div>
        </CardHeader>
        <CardContent className="aero:!p-4 aero:min-w-0 aero:overflow-x-auto">
          <Accordion type="multiple" className="aero:space-y-4">
            {filteredData?.map((shop) => (
              <AccordionItem
                key={shop.id}
                value={`shop-${shop.id}`}
                className="aero:border aero:border-gray-200 aero:rounded-lg aero:overflow-hidden"
              >
                <AccordionTrigger className="aero:px-6 aero:py-4 aero:hover:bg-gray-50 aero:transition-colors aero:[&[data-state=open]>div]:mb-0">
                  <div className="aero:flex aero:items-center aero:justify-between aero:w-full aero:mr-4">
                    <div className="aero:flex aero:items-center aero:gap-4 aero:w-full">
                      <div className="aero:flex aero:items-center aero:gap-2">
                        <h3 className="aero:text-lg aero:font-semibold aero:text-left">
                          {shop.shopName}
                        </h3>
                      </div>
                      <Badge
                        variant="secondary"
                        className="aero:bg-blue-100 aero:text-blue-800"
                      >
                        {shop.totalTechnicians} technicians
                      </Badge>
                      <div className="aero:ml-auto aero:flex aero:items-center aero:gap-2">
                        <Badge
                          variant="secondary"
                          className="aero:bg-green-100 aero:text-green-800 aero:pointer-events-none aero:hover:bg-inherit aero:hover:text-inherit"
                        >
                          {shop.availableTechnicians} Available
                        </Badge>
                        <Badge
                          variant="secondary"
                          className="aero:bg-red-100 aero:text-red-800 aero:pointer-events-none aero:hover:bg-inherit aero:hover:text-inherit"
                        >
                          {shop.unavailableTechnicians} Unavailable
                        </Badge>
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>

                <AccordionContent className="aero:px-6 aero:pb-4">
                  <div className="aero:grid aero:grid-cols-2 aero:gap-4 aero:p-2">
                    {shop.technicians.map((technician) => {
                      const isAvail = technician.isCurrentlyAvailable;
                      return (
                        <div
                          key={technician.id}
                          className="aero:flex aero:items-center aero:gap-3 aero:p-3 aero:border aero:rounded-lg aero:bg-gray-50"
                        >
                          <div className="aero:flex aero:items-center aero:justify-center aero:h-8 aero:w-8 aero:rounded-full aero:bg-blue-500 aero:text-white aero:text-sm aero:font-semibold">
                            {technician.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="aero:flex-1">
                            <div className="aero:font-medium aero:text-gray-900">
                              {technician.name}
                            </div>
                          </div>
                          <div className="aero:flex  aero:items-end aero:gap-1">
                            <Badge
                              className={
                                (isAvail
                                  ? "aero:bg-green-100 aero:text-green-800"
                                  : "aero:bg-red-100 aero:text-red-800") +
                                " aero:pointer-events-none aero:hover:bg-inherit aero:hover:text-inherit"
                              }
                            >
                              {isAvail ? "✓ Available" : "✗ Unavailable"}
                            </Badge>
                            {/* Shift/source temporarily hidden as per request */}
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              navigate({
                                to: "/plant-3/technicians/schedule",
                                search: {
                                  technicianId: String(technician.id),
                                },
                              })
                            }
                            className="aero:inline-flex aero:items-center aero:gap-1 aero:text-xs aero:px-2 aero:py-1 aero:rounded aero:border aero:bg-white aero:hover:bg-gray-100"
                          >
                            <Calendar className="aero:w-3 aero:h-3" />
                            <span>View Schedule</span>
                          </button>
                        </div>
                      );
                    })}

                    {shop.technicians.length === 0 && (
                      <div className="aero:text-center aero:text-gray-500 aero:py-8">
                        No technicians assigned to this shop
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {filteredData?.length === 0 && (
            <div className="aero:text-center aero:text-gray-500 aero:py-12">
              No shops found matching your criteria
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
